import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDesignStore } from '../../stores/designStore';
import type { IframeMessage } from '../../types';
import { ContentQualityFeedback } from './ContentQualityFeedback';

export const PreviewPanel: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { 
    previewHtml, 
    isGenerating, 
    previewError,
    currentDevice,
    themeConfig,
    setPreviewError,
  } = useDesignStore();

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState('100%');

  // 生成完整的HTML文档
  const generateFullHtml = useCallback(() => {
    if (!previewHtml) return '';

    const themeStyles = `
      :root {
        --primary-color: ${themeConfig.primaryColor};
        --secondary-color: ${themeConfig.secondaryColor};
        --background-color: ${themeConfig.backgroundColor};
        --text-color: ${themeConfig.textColor};
        --font-family: ${themeConfig.fontFamily};
        --border-radius: ${themeConfig.borderRadius};
      }
      body {
        font-family: var(--font-family);
        background-color: var(--background-color);
        color: var(--text-color);
        margin: 0;
        padding: 0;
      }
    `;

    // 如果previewHtml已经是完整HTML文档
    if (previewHtml.includes('<!DOCTYPE html>') || previewHtml.includes('<html')) {
      // 注入主题样式和iframe兼容性脚本
      let enhancedHtml = previewHtml;
      
      // 注入主题样式
      enhancedHtml = enhancedHtml.replace(
        '</head>',
        `<style>${themeStyles}</style></head>`
      );
      
      // 添加iframe兼容性脚本（在</body>前）
      const iframeCompatScript = `
<script>
  // iframe兼容性：确保动态添加的样式和脚本能正常工作
  (function() {
    // 保存原始的innerHTML方法
    const originalHeadInnerHTML = Object.getOwnPropertyDescriptor(HTMLHeadElement.prototype, 'innerHTML');
    const originalBodyInnerHTML = Object.getOwnPropertyDescriptor(HTMLBodyElement.prototype, 'innerHTML');
    
    // 重写head.innerHTML以支持动态样式
    if (originalHeadInnerHTML && originalHeadInnerHTML.set) {
      Object.defineProperty(HTMLHeadElement.prototype, 'innerHTML', {
        set: function(value) {
          // 创建临时div来解析HTML
          const temp = document.createElement('div');
          temp.innerHTML = value;
          
          // 将样式和脚本逐个添加到head
          Array.from(temp.children).forEach(child => {
            if (child.tagName === 'STYLE') {
              const style = document.createElement('style');
              style.textContent = child.textContent;
              this.appendChild(style);
            } else if (child.tagName === 'SCRIPT') {
              const script = document.createElement('script');
              script.textContent = child.textContent;
              this.appendChild(script);
            } else {
              this.appendChild(child);
            }
          });
          
          // 触发resize事件通知父窗口
          setTimeout(function() {
            if (window.parent !== window) {
              window.parent.postMessage({ type: 'resize', data: { height: document.body.scrollHeight } }, '*');
            }
          }, 100);
        },
        get: originalHeadInnerHTML.get
      });
    }
  })();
</script>`;
      
      enhancedHtml = enhancedHtml.replace('</body>', `${iframeCompatScript}</body>`);
      
      return enhancedHtml;
    }

    // 包装成完整HTML文档
    return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>预览</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" rel="stylesheet">
  <style>${themeStyles}</style>
</head>
<body>
  ${previewHtml}
  <script>
    // 向父窗口发送消息
    window.addEventListener('load', function() {
      window.parent.postMessage({ type: 'ready' }, window.parent.location.origin);
      
      // 发送页面高度
      const sendHeight = function() {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: 'resize', data: { height } }, window.parent.location.origin);
      };
      
      sendHeight();
      
      // 监听内容变化
      const observer = new MutationObserver(sendHeight);
      observer.observe(document.body, { childList: true, subtree: true });
      
      // 监听图片加载
      document.querySelectorAll('img').forEach(img => {
        img.addEventListener('load', sendHeight);
      });
    });
    
    // 错误处理
    window.addEventListener('error', function(e) {
      window.parent.postMessage({ 
        type: 'error', 
        data: { message: e.message, filename: e.filename, lineno: e.lineno }
      }, window.parent.location.origin);
    });
  </script>
</body>
</html>`;
  }, [previewHtml, themeConfig]);

  // 处理iframe加载完成
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  // 处理来自iframe的消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent<IframeMessage>) => {
      // 安全检查：验证消息来源
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const { type, data } = event.data;

      switch (type) {
        case 'ready':
          setIframeLoaded(true);
          break;
        case 'resize':
          if (data && typeof data === 'object' && 'height' in data) {
            setIframeHeight(`${(data as { height: number }).height + 50}px`);
          }
          break;
        case 'error':
          if (data && typeof data === 'object' && 'message' in data) {
            setPreviewError((data as { message: string }).message);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setPreviewError]);

  // 获取设备尺寸样式
  const getDeviceStyles = () => {
    switch (currentDevice) {
      case 'mobile':
        return {
          width: '375px',
          maxWidth: '100%',
          height: iframeHeight,
          minHeight: '667px',
          margin: '0 auto',
          border: '8px solid #1f2937',
          borderRadius: '24px',
          overflow: 'hidden',
        };
      case 'tablet':
        return {
          width: '768px',
          maxWidth: '100%',
          height: iframeHeight,
          minHeight: '1024px',
          margin: '0 auto',
          border: '12px solid #1f2937',
          borderRadius: '16px',
          overflow: 'hidden',
        };
      case 'desktop':
      default:
        return {
          width: '100%',
          height: '100%',
          minHeight: '600px',
        };
    }
  };

  const fullHtml = generateFullHtml();

  return (
    <div className="h-full flex flex-col bg-gray-100 p-4 overflow-auto">
      {/* 加载状态 */}
      {isGenerating && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-purple-200 rounded-full" />
              <div className="absolute inset-0 border-4 border-purple-500 rounded-full border-t-transparent animate-spin" />
            </div>
            <p className="text-gray-600 font-medium">AI正在生成设计...</p>
            <p className="text-sm text-gray-400 mt-1">请稍候</p>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {previewError && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg max-w-md">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">渲染出错</p>
              <p className="text-sm text-red-600 mt-1">{previewError}</p>
            </div>
          </div>
        </div>
      )}

      {/* 空状态 */}
      {!previewHtml && !isGenerating && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-2xl flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-500">预览区域</p>
            <p className="text-sm mt-2 max-w-xs mx-auto">
              在左侧与业务AI对话，或在底部输入设计指令来生成页面
            </p>
          </div>
        </div>
      )}

      {/* iframe预览 */}
      {previewHtml && (
        <div 
          className="transition-all duration-300 bg-white shadow-lg"
          style={getDeviceStyles()}
        >
          <iframe
            ref={iframeRef}
            srcDoc={fullHtml}
            onLoad={handleIframeLoad}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-forms allow-modals allow-popups"
            title="设计预览"
          />
        </div>
      )}

      {/* 加载中遮罩（iframe内部加载） */}
      {previewHtml && !iframeLoaded && (
        <div className="absolute inset-0 bg-white z-5 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-400">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>加载中...</span>
          </div>
        </div>
      )}

      {/* 内容质量反馈面板 */}
      {previewHtml && iframeLoaded && (
        <div className="absolute bottom-4 right-4 z-20 w-80">
          <ContentQualityFeedback
            html={previewHtml}
            onRegenerate={() => {
              // 触发重新生成逻辑
              window.dispatchEvent(new CustomEvent('ai:regenerate'));
            }}
            onModifyPrompt={() => {
              // 触发修改提示词逻辑
              window.dispatchEvent(new CustomEvent('ai:modifyPrompt'));
            }}
          />
        </div>
      )}
    </div>
  );
};
